import { useMemo } from "react";
import { css } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useMeetingPrepLearnedDefaults } from "@acto/taskpane/core/data-access/meeting-prep-learned-defaults";
import { useProductExclusionFeed } from "@acto/taskpane/core/data-access/product-exclusions";
import { Headline } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { LoadMoreFooter } from "@acto/taskpane/field-sales-2/components/primitives/LoadMoreFooter";
import { PageLayout } from "@acto/taskpane/field-sales-2/components/patterns/PageLayout";
import { ShellCloseButton, ShellTopBar } from "@acto/taskpane/field-sales-2/components/patterns/ShellTopBar";
import { hideDurationLabel } from "@acto/taskpane/field-sales-2/components/patterns/product-hide/hideDurationLabel";
import {
  ProfileEmptyState,
  ProfileHeader,
  ProfileSection,
  ProfileSections,
  useLearnedDefaultEntries,
  type ProfileEntry,
} from "@acto/taskpane/field-sales-2/components/patterns/ProfileSection";

const clearBottomNav = css(({ theme }) => ({
  "&&": {
    paddingBottom: `calc(var(--bottom-nav-opaque-height, 0px) + ${theme.fieldSalesTwo.spacing[24]})`,
  },
}));

const SEPARATOR = " · ";

export function UserProfile() {
  const t = useTranslations("fieldSalesTwo.userProfile");
  const tProductHide = useTranslations("fieldSalesTwo.genuiMeetingPrep.productHide");
  const tLearnedDefault = useTranslations("fieldSalesTwo.genuiMeetingPrep.learnedDefault");
  const { locale } = useLocale();
  const { data: rows, isPending, isFetching, error, hasNextPage, fetchNextPage } = useProductExclusionFeed();
  const {
    data: learnedDefaults,
    isPending: isLearnedDefaultsPending,
    error: learnedDefaultsError,
  } = useMeetingPrepLearnedDefaults();
  const learnedDefaultEntries = useLearnedDefaultEntries(learnedDefaults);

  const hiddenProducts = useMemo<ProfileEntry[]>(
    () =>
      (rows ?? []).map(({ id, exclusion, clientName, clientReference }) => ({
        id,
        title: exclusion.name(locale) ?? exclusion.productId,
        meta: [clientName, clientReference, hideDurationLabel(exclusion.duration(), tProductHide, locale)]
          .filter(Boolean)
          .join(SEPARATOR),
      })),
    [rows, locale, tProductHide],
  );

  const isEmpty = hiddenProducts.length === 0 && !hasNextPage && learnedDefaultEntries.length === 0;

  return (
    <>
      <ShellTopBar leading={<ShellCloseButton />} />
      <PageLayout
        hPadding="card"
        isLoading={isPending || isLearnedDefaultsPending}
        isError={(Boolean(error) && !rows?.length) || Boolean(learnedDefaultsError)}
        isEmpty={isEmpty}
        emptyState={<ProfileEmptyState title={t("title")} subtitle={t("emptySubtitle")} />}
        contentClassName={clearBottomNav}
      >
        <ProfileHeader>
          <Headline>{t("title")}</Headline>
        </ProfileHeader>
        <ProfileSections>
          <ProfileSection label={t("hiddenProducts")} entries={hiddenProducts} />
          <LoadMoreFooter hasMore={hasNextPage} isLoadingMore={isFetching && !isPending} onLoadMore={fetchNextPage} />
          <ProfileSection label={tLearnedDefault("title")} entries={learnedDefaultEntries} />
        </ProfileSections>
      </PageLayout>
    </>
  );
}
